import React from "react";
import { Link } from "@/lib/router";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash, ShoppingBag } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { SHOP } from "@/i18n/shop";
import { formatPrice } from "@/lib/storeApi";

export const MiniCart = () => {
  const { items, subtotal, open, setOpen, updateQty, removeItem, lineKey, count } = useCart();
  const { t, lang } = useLang();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm"
            data-testid="minicart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-[450px] flex-col border-l border-gold/10 bg-surface"
            data-testid="minicart-drawer"
          >
            <div className="flex items-center justify-between border-b border-gold/10 px-6 py-5">
              <span className="text-xs uppercase tracking-overline text-gold">
                {t(SHOP.cart.title)} {count > 0 && `· ${count}`}
              </span>
              <button onClick={() => setOpen(false)} data-testid="minicart-close" aria-label="Close" className="text-white/70 transition-colors hover:text-gold">
                <X size={22} weight="light" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 text-center">
                <ShoppingBag size={40} weight="light" className="text-gold/50" />
                <p className="text-sm text-white/60">{t(SHOP.cart.empty)}</p>
                <Link to="/shop" onClick={() => setOpen(false)} data-testid="minicart-continue" className="text-xs uppercase tracking-widest text-gold hover:text-gold-soft">
                  {t(SHOP.cart.continue)}
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {items.map((it) => {
                    const key = lineKey(it);
                    return (
                      <div key={key} data-testid={`minicart-item-${it.slug}`} className="flex gap-4 border-b border-gold/10 py-5">
                        <div className="h-20 w-20 shrink-0 overflow-hidden bg-ink">
                          {it.image && <img src={it.image} alt={t(it.name)} className="h-full w-full object-cover" />}
                        </div>
                        <div className="flex flex-1 flex-col">
                          <div className="flex justify-between gap-2">
                            <span className="font-display text-sm text-gold">{t(it.name)}</span>
                            <button onClick={() => removeItem(key)} data-testid={`minicart-remove-${it.slug}`} aria-label="Remove" className="text-white/40 transition-colors hover:text-gold">
                              <Trash size={16} weight="light" />
                            </button>
                          </div>
                          {it.variant_label && <span className="mt-0.5 text-xs text-white/40">{it.variant_label}</span>}
                          <div className="mt-auto flex items-center justify-end pt-3">
                            <div className="flex items-center border border-gold/20">
                              <button onClick={() => updateQty(key, it.quantity - 1)} data-testid={`minicart-dec-${it.slug}`} className="px-2 py-1.5 text-white/70 hover:text-gold">
                                <Minus size={13} weight="light" />
                              </button>
                              <span className="min-w-[28px] text-center text-xs text-white">{it.quantity}</span>
                              <button onClick={() => updateQty(key, it.quantity + 1)} data-testid={`minicart-inc-${it.slug}`} className="px-2 py-1.5 text-white/70 hover:text-gold">
                                <Plus size={13} weight="light" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-gold/10 px-6 py-6">
                  <Link
                    to="/checkout"
                    onClick={() => setOpen(false)}
                    data-testid="minicart-checkout"
                    className="mt-5 block bg-gold px-6 py-4 text-center text-xs uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-gold-muted"
                  >
                    {t(SHOP.cart.checkout)}
                  </Link>
                  <Link to="/cart" onClick={() => setOpen(false)} data-testid="minicart-viewcart" className="mt-3 block text-center text-xs uppercase tracking-widest text-white/50 hover:text-gold">
                    {t(SHOP.cart.title)}
                  </Link>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
